import { ChevronRightIcon } from '@vizit/icons';
import { cx } from '@vizit/theme';
import type * as React from 'react';

interface CategoryBreadcrumbsProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Category names ordered from the top-level (L1) down to the leaf (e.g. L4 or L5).
   * Typically the product category's comma-separated `breadcrumbs` split into parts.
   * @type {string[]}
   * @required
   */
  levels: string[];
}

/**
 * Renders a product category's breadcrumbs as a trail (L1 › L2 › … › leaf),
 * with a chevron between levels. Intended as tooltip content for a Category value
 * (product page and image details headers).
 */
function CategoryBreadcrumbs({ levels, className, ...other }: CategoryBreadcrumbsProps) {
  return (
    <div className={cx('flex flex-wrap items-center gap-1 max-w-[420px]', className)} {...other}>
      {levels.map((level, index) => {
        const isLast = index === levels.length - 1;
        return (
          <span key={`${index}-${level}`} className="flex items-center gap-1">
            <span className={cx('whitespace-nowrap', isLast ? 'font-semibold' : 'text-gray-utility-300')}>{level}</span>
            {!isLast && <ChevronRightIcon size={12} className="shrink-0 text-gray-utility-400" aria-hidden />}
          </span>
        );
      })}
    </div>
  );
}

export { CategoryBreadcrumbs };
export type { CategoryBreadcrumbsProps };
